'use client';

/**
 * ListingCard Component
 *
 * A card component for displaying activity listings with instructor details,
 * location, bookings, and price information.
 */
import { Briefcase, Calendar, ChevronRight, Clock, Home, MapPin, User, Video } from 'lucide-react';
import React from 'react';

import { interceptLinkClick } from '../../../../lib/linkClick';
import { Button } from '../../../common/button/Button';
import { ImageWithFallback } from '../../../common/imageWithFallback/ImageWithFallback';
import StarFilled from '../../../icons/StarFilled';
import { Badge } from '../../../ui/badge';
import { ActivityMobileCard } from '../../activity/activityMobileCard/ActivityMobileCard';
import { type LocationKind, LocationKindChips } from '../../activity/locationKindChips';
import { type ActivityAvailability, UnavailableBadge } from '../../activity/unavailableBadge';

export interface ListingCardProps {
  /**
   * Title of the activity listing
   * @default "DDP Spiritual Yoga"
   */
  title: string;

  /**
   * Name of the instructor
   */
  instructor: string;

  /**
   * Whether to blur the instructor name (subscription limit)
   */
  blurInstructor?: boolean;

  /**
   * Whether to hide the instructor name entirely (e.g. on professional profile page)
   */
  hideInstructor?: boolean;

  /**
   * Prefix before the professional name (e.g. "Par", "By")
   */
  professionalPrefix?: string;

  /**
   * Location of the activity
   * @default "At Home"
   */
  location: string;

  /**
   * Booking information
   * @default "22 Bookings in 2024"
   */
  bookings: string;

  /**
   * Price information (formatted currency string, e.g. "40 €")
   */
  price: string;

  /**
   * Prefix label for price (e.g. "À partir de", "From")
   */
  pricePrefix?: string;

  /**
   * URL for the listing image
   * If not provided, a placeholder will be displayed
   */
  imageUrl?: string;

  /**
   * Square-framed image for the collapsed card's thumbnail. Falls back to
   * `imageUrl` (center-cropped) when the activity has no dedicated frame.
   */
  thumbnailUrl?: string;

  /**
   * Optional click handler for the card
   */
  onClick?: () => void;

  /**
   * URL of the activity page. When set (and not on a booking page), the card
   * exposes a real crawlable link: plain left clicks still call `onClick` /
   * `onViewActivity`, while cmd/ctrl/middle clicks open the URL natively.
   */
  href?: string;

  /**
   * Optional hover handler for the card
   */
  onHover?: () => void;

  /**
   * Optional handler for when hover leaves the card
   */
  onLeaveHover?: () => void;

  /**
   * Whether this card is currently being hovered
   */
  isHovered?: boolean;

  /**
   * Whether this card is currently selected
   */
  isSelected?: boolean;

  /**
   * Handler for viewing the full activity details
   */
  onViewActivity?: () => void;

  /**
   * Handler for viewing available slots
   */
  onViewSlots?: () => void;

  /**
   * Whether the activity is at home (used for expanded view)
   */
  isAtHome?: boolean;

  /**
   * Whether this booking is online (visio). When true, a violet "online" chip
   * replaces the at-home / at-venue location row.
   */
  isOnline?: boolean;

  /**
   * The activity's real delivery-mode MIX (venue / at_home / online). When it
   * holds 2+ kinds the location row renders one compact chip per kind instead
   * of the single `isAtHome`/`isOnline` indicator. A single-kind (or absent)
   * value keeps the legacy single-indicator look. Drives the search + expanded
   * activity-card views (not the per-booking booking-page row).
   */
  locationKinds?: LocationKind[];

  /** Chip labels for the location-kind mix (venue / at_home / online). */
  locationKindLabels?: { venue: string; at_home: string; online: string };

  /**
   * Label for the at-home badge overlay on the thumbnail
   * @default "À domicile"
   */
  atHomeLabel?: string;

  /**
   * Label shown for an online (visio) booking.
   * @default "Online"
   */
  onlineLabel?: string;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Translated text for "View Activity" button
   * @default "View Activity"
   */
  viewActivityButtonText?: string;

  /**
   * Translated text for "Available Slots" button
   * @default "Available Slots"
   */
  availableSlotsButtonText?: string;

  /**
   * Whether this card is displayed on a booking page
   * If true, the card will not expand even when selected
   * @default false
   */
  isBookingPage?: boolean;

  /**
   * Date information for booking pages
   * Only displayed when isBookingPage is true
   */
  date?: string;

  /**
   * Time information for booking pages
   * Only displayed when isBookingPage is true
   */
  time?: string;

  /**
   * Time ago information for booking pages
   * Shows how long ago the booking was made/updated
   * Only displayed when isBookingPage is true
   */
  timeAgo?: string;

  /**
   * Whether this booking is upcoming
   * Used for styling the timeAgo badge
   * @default true
   */
  isUpcoming?: boolean;

  /**
   * Whether this booking is currently ongoing (started but not ended)
   * Used for styling the timeAgo badge in blue
   * @default false
   */
  isOngoing?: boolean;

  /**
   * Optional badge element to display (e.g., payment status)
   */
  badge?: React.ReactNode;

  /**
   * Handler for Pay Now action (shown when payment is pending)
   */
  onPayNow?: () => void;

  /**
   * Text for the Pay Now button
   * @default "Pay Now"
   */
  payNowText?: string;

  /**
   * Average rating (0-5) for the activity
   */
  ratingAverage?: number;

  /**
   * Total number of ratings
   */
  ratingTotal?: number;

  /**
   * Translated label shown above the price on the mobile booking card.
   * @default "Price"
   */
  priceLabel?: string;

  /**
   * Label of the muted badge a not-bookable-yet card (`availability: 'none'`)
   * shows in place of its price. Without it such a card shows nothing there.
   */
  unavailableText?: string;

  /**
   * Whether the activity can be booked right now. A 'none' card shows no price
   * (the `unavailableText` badge instead, when given) and its expanded view
   * drops the "available slots" action, which would lead to an empty picker.
   */
  availability?: ActivityAvailability;

  /**
   * Short muted context after the instructor on the search card, e.g. why a
   * suggestion is shown ("Nearby", "Popular").
   */
  contextLabel?: string;

  /**
   * Optional action menu (e.g. 3-dot dropdown) rendered in the top-right of
   * the desktop booking-page card. Only shown when `isBookingPage` is true
   * and the card is in compact view. The element should stop propagation on
   * its own clicks so the card's onClick isn't triggered.
   */
  actionMenu?: React.ReactNode;
}

/**
 * ListingCardSkeleton component for displaying a loading state
 */
export const ListingCardSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div
      className={`flex border border-neutral-200 rounded-xl overflow-hidden w-full h-[132px] bg-white ${className || ''}`}
    >
      {/* Thumbnail skeleton */}
      <div className="h-full w-[120px] bg-gray-200 animate-pulse rounded-l-xl" />

      {/* Details skeleton */}
      <div className="flex flex-col p-3 gap-2 flex-1">
        {/* Title and instructor skeleton */}
        <div className="flex flex-col gap-2">
          <div className="h-5 bg-gray-200 rounded-md w-3/4 animate-pulse" />
          <div className="h-3 bg-gray-200 rounded-md w-1/2 animate-pulse" />
        </div>

        {/* Other details skeleton */}
        <div className="flex flex-col gap-2 mt-1">
          {/* Location skeleton */}
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse" />
            <div className="h-3 bg-gray-200 rounded-md w-1/3 animate-pulse" />
          </div>

          {/* Bookings skeleton */}
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse" />
            <div className="h-3 bg-gray-200 rounded-md w-2/5 animate-pulse" />
          </div>

          {/* Price skeleton */}
          <div className="flex items-center">
            <div className="h-4 bg-gray-200 rounded-md w-1/4 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ListingCard component for displaying activity listings
 *
 * @example
 * ```tsx
 * <ListingCard
 *   title="DDP Spiritual Yoga"
 *   instructor="Marie Dupont"
 *   location="At Home"
 *   bookings="22 Bookings in 2024"
 *   price="€40.00 onwards"
 *   imageUrl="/images/yoga.jpg"
 *   onClick={() => console.log('Card clicked')}
 * />
 * ```
 */
export const ListingCard: React.FC<ListingCardProps> = React.memo(
  ({
    title = 'DDP Spiritual Yoga',
    instructor = 'Marie Dupont',
    blurInstructor = false,
    hideInstructor = false,
    professionalPrefix: _professionalPrefix,
    location = 'At Home',
    bookings = '22 Bookings in 2024',
    price = '40 €',
    pricePrefix,
    imageUrl,
    thumbnailUrl,
    onClick,
    href,
    onHover,
    onLeaveHover,
    isHovered = false,
    isSelected = false,
    onViewActivity,
    onViewSlots,
    isAtHome = false,
    isOnline = false,
    locationKinds,
    locationKindLabels,
    atHomeLabel: _atHomeLabel = 'À domicile',
    onlineLabel = 'Online',
    className,
    viewActivityButtonText = 'View Activity',
    availableSlotsButtonText = 'Available Slots',
    isBookingPage = false,
    date,
    time,
    timeAgo,
    isUpcoming = true,
    isOngoing = false,
    badge,
    onPayNow,
    payNowText = 'Pay Now',
    ratingAverage,
    ratingTotal,
    priceLabel = 'Price',
    actionMenu,
    unavailableText,
    availability,
    contextLabel
  }) => {
    // Determine if we should show the expanded view
    // Don't expand if it's a booking page, even when selected
    const isExpanded = isSelected && !isBookingPage;
    const linkHref = isBookingPage ? undefined : href;

    // Always use location (Online/At Home/At Venue) - never show full address
    const displayLocation = isOnline ? onlineLabel : location;

    // A mixed activity (2+ delivery kinds) renders one compact chip per kind via
    // LocationKindChips; otherwise we keep the legacy single indicator (which can
    // show a city for venue). Only the activity-discovery views opt in; the
    // booking-page row still shows the one booked location.
    const showKindMix = Boolean(locationKindLabels) && (locationKinds?.length ?? 0) >= 2;

    const renderCompactView = () => {
      if (isBookingPage) {
        return (
          <>
            {/* Mobile Booking View - ActivityMobileCard */}
            <div className="md:hidden">
              <ActivityMobileCard
                title={title}
                professionalName={instructor}
                imageUrl={imageUrl}
                isAtHome={isAtHome}
                isOnline={isOnline}
                startingPrice={price}
                date={date}
                time={time}
                timeAgo={timeAgo}
                isUpcoming={isUpcoming}
                isOngoing={isOngoing}
                isFromBookingPage={true}
                hideProfessionalName={true}
                showPriceFrom={false}
                onClick={onClick}
                badge={badge}
                onPayNow={onPayNow}
                payNowText={payNowText}
                labels={{
                  atHomeBadge: displayLocation,
                  priceLabel,
                  priceFrom: '',
                  contactForPrice: 'Contact'
                }}
              />
            </div>

            {/* Desktop Booking View */}
            <div className="hidden md:flex w-full md:h-[132px] cursor-pointer relative" onClick={onClick}>
              {/* Action menu - tab-style, attached to the top-right corner of
                  the card. Sits on top of the content and is clipped by the
                  parent's rounded corner via overflow-hidden. */}
              {actionMenu && <div className="absolute top-0 right-0 z-10">{actionMenu}</div>}

              {/* Thumbnail */}
              <div className="h-full w-[120px] md:aspect-square bg-gray-200 flex-shrink-0 relative overflow-hidden rounded-l-xl">
                <ImageWithFallback
                  src={thumbnailUrl ?? imageUrl}
                  alt={title}
                  className="h-full w-full object-cover"
                  sizes="120px"
                />
                {/* TimeAgo badge - top left of thumbnail */}
                {timeAgo && (
                  <div className="absolute top-2 left-2">
                    <Badge
                      className={`backdrop-blur-sm border-0 text-xs px-2 py-1 flex items-center gap-1 rounded-lg pointer-events-none ${
                        isOngoing
                          ? 'bg-info-100/90 text-info-700'
                          : isUpcoming
                            ? 'bg-success-100/90 text-success-700'
                            : 'bg-neutral-100/90 text-neutral-600'
                      }`}
                    >
                      <Clock
                        className={`w-3 h-3 ${
                          isOngoing ? 'text-info-600' : isUpcoming ? 'text-success-600' : 'text-neutral-600'
                        }`}
                      />
                      {timeAgo}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="flex flex-col px-3 pt-2 pb-3 gap-1.5 flex-1 min-w-0">
                {/* Title row - pay CTA sits next to the title; the action
                    menu is absolutely positioned on the card corner. Right
                    padding reserves room for the action tab so the title
                    never collides with it. */}
                <div className="flex items-start justify-between">
                  <h3
                    className={`text-lg font-semibold text-neutral-800 truncate leading-tight flex-1 min-w-0 ${actionMenu && !onPayNow ? 'pr-24' : ''}`}
                  >
                    {title}
                  </h3>
                  {onPayNow && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPayNow();
                      }}
                      className={`bg-primary-500 hover:bg-primary-600 text-white font-medium px-4 py-1.5 rounded-lg text-sm transition-colors flex-shrink-0 ml-2 ${actionMenu ? 'mr-24' : ''}`}
                    >
                      {payNowText}
                    </button>
                  )}
                </div>

                {/* Two-column meta section below the title:
                    - Left column: user, address, date (stacked, left-aligned).
                    - Right column: price stacked above the payment-method
                      badge, right-aligned and flush to the card edge. */}
                <div className="flex items-end justify-between gap-3 flex-1 min-w-0">
                  <div className="flex flex-col gap-1.5 text-sm text-neutral-600 min-w-0 flex-1">
                    {!hideInstructor && (
                      <div
                        className={`flex items-center gap-1 min-w-0 ${blurInstructor ? '[filter:blur(4px)] select-none' : ''}`}
                      >
                        <User className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{instructor}</span>
                      </div>
                    )}
                    {displayLocation && (
                      <div className={`flex items-center gap-1 min-w-0 ${isOnline ? 'text-violet-700' : ''}`}>
                        {isOnline ? (
                          <Video className="w-4 h-4 flex-shrink-0 text-violet-600" />
                        ) : isAtHome ? (
                          <Home className="w-4 h-4 flex-shrink-0" />
                        ) : (
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                        )}
                        <span className="truncate">{displayLocation}</span>
                      </div>
                    )}
                    {date && (
                      <div className="flex items-center gap-1 min-w-0">
                        <Calendar className="w-4 h-4 flex-shrink-0" />
                        <span className="whitespace-nowrap truncate">
                          {date} <span className="lg:inline hidden">• {time}</span>
                        </span>
                      </div>
                    )}
                  </div>
                  {(badge || (!onPayNow && price)) && (
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      {!onPayNow && price && (
                        <span className="whitespace-nowrap leading-none">
                          {pricePrefix && <span className="text-xs font-normal text-neutral-400">{pricePrefix} </span>}
                          <span className="text-sm font-semibold text-neutral-800">{price}</span>
                        </span>
                      )}
                      {badge}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      }

      // Search Card View
      return (
        <div
          className="relative flex w-full h-[92px] md:h-[112px] cursor-pointer"
          onClick={linkHref ? undefined : onClick}
        >
          {/* Thumbnail - square at all resolutions */}
          <div className="h-full aspect-square bg-gray-200 flex-shrink-0 relative overflow-hidden rounded-l-xl">
            <ImageWithFallback
              src={thumbnailUrl ?? imageUrl}
              alt={title}
              className="h-full w-full object-cover"
              sizes="112px"
            />
            {ratingAverage != null && ratingTotal != null && ratingTotal > 0 && (
              <span className="absolute top-2 left-0 bg-white px-1.5 py-1 rounded-r flex items-center gap-1">
                <span className="text-xs font-medium text-neutral-600">{ratingAverage.toFixed(1)}</span>
                <StarFilled className="w-4 h-4 text-[#FDC812]" />
              </span>
            )}
          </div>

          {/* Details - Title, Author, then [Location | Price] row */}
          <div className="flex flex-col p-3 md:px-4 md:py-3 flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-neutral-800 truncate leading-snug">
              {linkHref ? (
                <a
                  href={linkHref}
                  draggable={false}
                  onClick={(e) => interceptLinkClick(e, onClick)}
                  className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:rounded-xl focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-primary-500"
                >
                  {title}
                </a>
              ) : (
                title
              )}
            </h3>
            {(!hideInstructor || contextLabel) && (
              <p className="text-xs md:text-sm font-medium text-neutral-600 truncate">
                {!hideInstructor && (
                  <span className={blurInstructor ? '[filter:blur(4px)] select-none' : undefined}>{instructor}</span>
                )}
                {contextLabel && (
                  <span className="font-normal text-neutral-400">
                    {!hideInstructor && ' · '}
                    {contextLabel}
                  </span>
                )}
              </p>
            )}
            <div className="mt-auto flex items-end justify-between gap-2 min-w-0">
              {showKindMix && locationKindLabels ? (
                <LocationKindChips kinds={locationKinds ?? []} labels={locationKindLabels} className="flex-1" />
              ) : (
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  {isOnline ? (
                    <Video className="w-4 h-4 flex-shrink-0 text-violet-600" />
                  ) : isAtHome ? (
                    <Home className="w-4 h-4 flex-shrink-0 text-neutral-600" />
                  ) : (
                    <MapPin className="w-4 h-4 flex-shrink-0 text-neutral-600" />
                  )}
                  <span
                    className={`text-xs md:text-sm font-medium truncate ${isOnline ? 'text-violet-700' : 'text-neutral-600'}`}
                  >
                    {displayLocation}
                  </span>
                </div>
              )}
              {availability === 'none'
                ? unavailableText && <UnavailableBadge text={unavailableText} className="flex-shrink-0" />
                : price && (
                    <span className="flex items-baseline gap-1 whitespace-nowrap flex-shrink-0">
                      {pricePrefix && (
                        <span className="text-xs md:text-sm font-normal text-neutral-600">{pricePrefix}</span>
                      )}
                      <span className="text-sm md:text-base font-semibold text-primary">{price}</span>
                    </span>
                  )}
            </div>
          </div>
        </div>
      );
    };

    const renderExpandedView = () => {
      const heroContent = (
        <>
          <div className="absolute inset-0">
            <ImageWithFallback
              src={imageUrl}
              alt={linkHref ? '' : title}
              className="w-full h-full object-cover"
              sizes="100vw"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/70"></div>
          </div>

          <div className="relative h-full p-4 flex flex-col justify-between">
            <div className="flex justify-between items-start">
              {(isAtHome || isOnline) && (
                <div className="flex items-center bg-black/50 backdrop-blur-sm p-1.5 rounded-md text-xs max-w-[70%]">
                  {isOnline ? (
                    <Video size={14} className="mr-1.5 flex-shrink-0" />
                  ) : (
                    <Home size={14} className="mr-1.5 flex-shrink-0" />
                  )}
                  <span className="truncate">{displayLocation}</span>
                </div>
              )}
              {availability === 'none' && unavailableText && (
                <UnavailableBadge text={unavailableText} className="ml-auto" />
              )}
            </div>
            <div>
              <div className="flex items-center text-left">
                <h1 className="text-2xl font-bold text-white/90 group-hover:text-white transition-colors duration-200">
                  {title}
                </h1>
                <ChevronRight
                  size={32}
                  className="ml-1 flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200"
                />
              </div>
              {!hideInstructor && (
                <p className={`text-base ${blurInstructor ? '[filter:blur(4px)] select-none' : ''}`}>{instructor}</p>
              )}
            </div>
          </div>
        </>
      );

      return (
        <>
          {/* Image and Overlaid Content - entire area clicks to view activity */}
          {linkHref ? (
            <a
              href={linkHref}
              draggable={false}
              onClick={(e) => interceptLinkClick(e, onViewActivity)}
              className="group relative block h-[200px] text-white overflow-hidden cursor-pointer"
            >
              {heroContent}
            </a>
          ) : (
            <div
              className="group relative h-[200px] text-white overflow-hidden cursor-pointer"
              onClick={onViewActivity}
              role="button"
              tabIndex={0}
            >
              {heroContent}
            </div>
          )}

          {/* Basic Info Section */}
          <div className="p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between min-w-0 gap-2">
              {showKindMix && locationKindLabels ? (
                <LocationKindChips kinds={locationKinds ?? []} labels={locationKindLabels} className="flex-1" />
              ) : (
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  {isOnline ? (
                    <Video className="w-4 h-4 text-violet-600 flex-shrink-0" />
                  ) : isAtHome ? (
                    <Home className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                  ) : (
                    <MapPin className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                  )}
                  <span className={`text-sm truncate ${isOnline ? 'text-violet-700' : 'text-neutral-600'}`}>
                    {displayLocation}
                  </span>
                </div>
              )}
              {price && (
                <span className="whitespace-nowrap flex-shrink-0">
                  {pricePrefix && <span className="text-xs font-normal text-neutral-400">{pricePrefix} </span>}
                  <span className="text-sm font-semibold text-primary">{price}</span>
                </span>
              )}
            </div>
            {bookings && (
              <div className="flex items-center gap-1">
                <Briefcase className="w-4 h-4 text-neutral-600" />
                <span className="text-sm text-neutral-600">{bookings}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-4 mt-auto bg-white border-t border-neutral-200 flex space-x-3">
            <Button
              variant="secondary"
              label={viewActivityButtonText}
              onClick={onViewActivity}
              className="flex-1 text-sm md:text-base"
            />
            {availability !== 'none' && (
              <Button
                variant="primary"
                label={availableSlotsButtonText}
                onClick={onViewSlots}
                className="flex-1 text-sm md:text-base"
              />
            )}
          </div>
        </>
      );
    };

    return (
      <div
        className={`flex flex-col ${
          // For booking pages on mobile, remove border styling completely
          isBookingPage
            ? // Booking view: flat card, no hover/selection highlight beyond
              // the selected-state border. We intentionally skip the
              // cross-component (isHovered) and CSS :hover primary highlight
              // here - the bookings list row already signals selection on
              // click and constant hover feedback adds visual noise.
              'md:border md:overflow-hidden' +
              (isSelected
                ? ' rounded-2xl md:border-primary-400 md:border-2 md:shadow-lg'
                : ' rounded-xl md:border-neutral-200')
            : // For search pages, keep normal border styling
              'border overflow-hidden ' +
              (isSelected
                ? 'rounded-2xl border-primary-400 border-2 shadow-lg'
                : 'rounded-xl border-neutral-200' +
                  // Cross-component highlight (e.g. map marker hovered)
                  (isHovered ? ' border-primary-500 shadow-md' : '') +
                  // CSS hover for instant self-hover effect
                  ' hover:border-primary-500 hover:shadow-md')
        } w-full ${isExpanded ? 'h-auto' : isBookingPage ? 'md:h-[132px]' : 'md:h-[112px]'} bg-white ${className || ''}`}
        onClick={!isExpanded && !linkHref ? onClick : undefined}
        onMouseEnter={onHover}
        onMouseLeave={onLeaveHover}
        role={onClick && !linkHref ? 'button' : undefined}
        tabIndex={onClick && !linkHref ? 0 : undefined}
      >
        {isExpanded ? renderExpandedView() : renderCompactView()}
      </div>
    );
  }
);

ListingCard.displayName = 'ListingCard';
